import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';

interface DateSelectorProps {
  selectedDate: string;
  dates?: string[];
  onDateChange: (date: string) => void;
  label?: string;
  id?: string;
  className?: string;
  compact?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const parseDateParts = (dateStr?: string) => {
  if (!dateStr || !dateStr.includes('-')) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() };
  }
  const parts = dateStr.split('-').map(Number);
  return {
    year: parts[0] || new Date().getFullYear(),
    month: (parts[1] || 1) - 1,
    day: parts[2] || 1,
  };
};

/**
 * Date selector component supporting custom calendar popover with non-data dates disabled
 */
const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  dates,
  onDateChange,
  label = 'Select Date:',
  id = 'date-select',
  className = 'date-dropdown',
  compact = false,
}) => {
  const { data: analyticsData, ensureFullHistoryLoaded, isFullHistoryLoaded } = useAnalyticsData();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  // Proactively fetch full history when the calendar popover opens if archive isn't loaded yet
  useEffect(() => {
    if (isOpen && !isFullHistoryLoaded && ensureFullHistoryLoaded) {
      void ensureFullHistoryLoaded();
    }
  }, [isOpen, isFullHistoryLoaded, ensureFullHistoryLoaded]);

  // Derive unique, chronologically sorted available dates (newest first)
  const availableDates = useMemo(() => {
    const source = dates && dates.length > 0 ? dates : (analyticsData ?? []).map((item) => item.Date);
    const set = new Set(source.filter(Boolean));
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [dates, analyticsData]);

  const availableDatesSet = useMemo(() => new Set(availableDates), [availableDates]);

  // Calendar month/year navigation state
  const initialParts = useMemo(() => parseDateParts(selectedDate || availableDates[0]), [selectedDate, availableDates]);
  const [viewYear, setViewYear] = useState(initialParts.year);
  const [viewMonth, setViewMonth] = useState(initialParts.month);

  // Sync calendar view month/year when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      const { year, month } = parseDateParts(selectedDate);
      setViewYear(year);
      setViewMonth(month);
    }
  }, [selectedDate]);

  // Close calendar popover on click outside or Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const cleanLabel = (label || 'Date').replace(/select/i, '').replace(/:$/, '').trim() || 'Date';


  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isFullHistoryLoaded && ensureFullHistoryLoaded) {
      void ensureFullHistoryLoaded();
    }
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleJumpToLatest = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (availableDates.length > 0) {
      onDateChange(availableDates[0]);
      setIsOpen(false);
    }
  };

  // Extract list of distinct years with data for dropdown (available to all users)
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    availableDates.forEach((d) => {
      const yr = Number(d.split('-')[0]);
      if (!isNaN(yr)) years.add(yr);
    });
    const currentYear = new Date().getFullYear();
    for (let y = currentYear - 4; y <= currentYear; y++) {
      years.add(y);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [availableDates]);

  // Calendar grid calculation (Monday-first: 0 = Mon, ..., 6 = Sun)
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const startingDay = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
  const leadingDays = Array.from({ length: startingDay }, (_, i) => {
    const day = prevMonthDays - startingDay + 1 + i;
    const m = viewMonth === 0 ? 11 : viewMonth - 1;
    const y = viewMonth === 0 ? viewYear - 1 : viewYear;
    const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayOfWeek = i % 7;
    return {
      day,
      dateStr,
      isCurrentMonth: false,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      hasData: availableDatesSet.has(dateStr),
    };
  });

  const currentDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayOfWeek = (startingDay + i) % 7;
    return {
      day,
      dateStr,
      isCurrentMonth: true,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      hasData: availableDatesSet.has(dateStr),
    };
  });

  const totalCells = leadingDays.length + currentDays.length;
  const trailingCount = (7 - (totalCells % 7)) % 7;
  const trailingDays = Array.from({ length: trailingCount }, (_, i) => {
    const day = i + 1;
    const m = viewMonth === 11 ? 0 : viewMonth + 1;
    const y = viewMonth === 11 ? viewYear + 1 : viewYear;
    const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayOfWeek = (totalCells + i) % 7;
    return {
      day,
      dateStr,
      isCurrentMonth: false,
      isWeekend: dayOfWeek === 5 || dayOfWeek === 6,
      hasData: availableDatesSet.has(dateStr),
    };
  });

  const allGridDays = [...leadingDays, ...currentDays, ...trailingDays];

  return (
    <div
      ref={containerRef}
      className={`date-selector-container ${compact ? 'date-selector-container--compact' : ''}`}
      style={{ position: 'relative' }}
    >
      {!compact && label && (
        <label htmlFor={id} className="date-label">
          {cleanLabel}
        </label>
      )}

      <div className={`date-selector-field-group ${compact ? 'date-selector-field-group--compact' : ''}`}>
        <button
          type="button"
          id={id}
          onClick={() => setIsOpen((prev) => !prev)}
          className={`date-selector-trigger ${compact ? 'date-selector-trigger--compact' : ''} ${className}`}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={cleanLabel || 'Select Date'}
          title="Click to open market session calendar"
        >
          <Calendar size={compact ? 13 : 14} className="date-selector-trigger__icon" style={{ color: 'var(--brand-blue, #0284c7)' }} />
          <span className="date-selector-trigger__text">{selectedDate || 'Select Date'}</span>
          {compact && <span className="date-selector__tz-pill">IST</span>}
          <ChevronDown
            size={compact ? 11 : 13}
            className={`date-selector-chevron ${isOpen ? 'date-selector-chevron--open' : ''}`}
          />
        </button>
      </div>

      {isOpen && (
        <div
          className={`date-selector-popover ${compact ? 'date-selector-popover--compact' : ''}`}
          role="dialog"
          aria-label="Market session calendar"
        >
          {/* Header with Month/Year Navigation */}
          <div className="date-picker-header">
            <button
              type="button"
              className="date-picker-nav-btn"
              onClick={handlePrevMonth}
              title="Previous month"
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>

            <div className="date-picker-title-group">
              <select
                className="date-picker-select date-picker-select--month"
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                aria-label="Select month"
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={name} value={idx}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                className="date-picker-select date-picker-select--year"
                value={viewYear}
                onChange={(e) => {
                  const nextYr = Number(e.target.value);
                  setViewYear(nextYr);
                  if (!isFullHistoryLoaded && ensureFullHistoryLoaded) {
                    void ensureFullHistoryLoaded();
                  }
                }}
                aria-label="Select year"
              >
                {availableYears.map((yr) => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="date-picker-nav-btn"
              onClick={handleNextMonth}
              title="Next month"
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Weekday Names (Monday-first) */}
          <div className="date-picker-weekdays" aria-hidden="true">
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, idx) => (
              <span
                key={day}
                className={`date-picker-weekday ${idx >= 5 ? 'date-picker-weekday--weekend' : ''}`}
              >
                {day}
              </span>
            ))}
          </div>

          {/* Days Grid: Non-data dates are explicitly DISABLED but CLEARLY VISIBLE */}
          <div className="date-picker-grid">
            {allGridDays.map(({ day, dateStr, isCurrentMonth, isWeekend, hasData }) => {
              const isSelected = dateStr === selectedDate;

              return (
                <button
                  key={dateStr}
                  type="button"
                  disabled={!hasData}
                  onClick={() => {
                    if (hasData) {
                      onDateChange(dateStr);
                      setIsOpen(false);
                    }
                  }}
                  className={`date-picker-day ${
                    isSelected
                      ? 'date-picker-day--selected'
                      : hasData
                      ? 'date-picker-day--available'
                      : 'date-picker-day--disabled'
                  } ${isWeekend ? 'date-picker-day--weekend' : ''} ${
                    !isCurrentMonth ? 'date-picker-day--adjacent' : ''
                  }`}
                  title={
                    hasData
                      ? `${dateStr} · Market session available`
                      : isWeekend
                      ? `${dateStr} · Weekend (Market Closed)`
                      : `${dateStr} · No session data (Market Closed)`
                  }
                  aria-label={`${dateStr}${hasData ? ' (active session)' : ' (closed, no data)'}`}
                >
                  <span className="date-picker-day__number">{day}</span>
                </button>
              );
            })}
          </div>

          {/* Popover Footer with Quick Jump to Latest */}
          <div className="date-picker-footer">
            <button
              type="button"
              className="date-picker-latest-btn"
              onClick={handleJumpToLatest}
              title={`Jump to latest session (${availableDates[0] || 'Today'})`}
            >
              Latest: {availableDates[0] || 'Today'}
            </button>
            <div className="date-picker-legend">
              <span className="date-picker-legend-item">
                <span className="date-picker-legend__dot date-picker-legend__dot--active" aria-hidden="true" />
                <span>Trading Day</span>
              </span>
              <span className="date-picker-legend-item">
                <span className="date-picker-legend__dot date-picker-legend__dot--closed" aria-hidden="true" />
                <span>Closed</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateSelector;
